import React, { useState, useEffect } from 'react';
import { FiPlus, FiBell, FiTrash2, FiEdit2, FiPlay, FiPause, FiRefreshCw, FiX, FiCheck } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Monitors.css';

const Monitors = ({ onBack }) => {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    query_string: '',
    filters_json: '',
    cadence: 'weekly',
    email_notifications: true,
    email_address: ''
  });

  useEffect(() => {
    fetchMonitors();
    
    // Check if there's a pending monitor from "Save This Search"
    const pendingMonitor = sessionStorage.getItem('pendingMonitor');
    if (pendingMonitor) {
      try {
        const params = JSON.parse(pendingMonitor);
        setFormData({
          name: params.query_string || 'Saved Search',
          query_string: params.query_string || '',
          filters_json: params.filters_json || '',
          cadence: 'weekly',
          email_notifications: true,
          email_address: ''
        });
        setShowCreateModal(true);
        sessionStorage.removeItem('pendingMonitor');
      } catch (e) {
        console.error('Error parsing pending monitor:', e);
      }
    }
  }, []);

  const fetchMonitors = async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint('/api/monitors'), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch monitors');
      const data = await response.json();
      setMonitors(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint('/api/monitors'), {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create monitor');
      }
      await fetchMonitors();
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/monitors/${editingMonitor.id}`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update monitor');
      }
      await fetchMonitors();
      setEditingMonitor(null);
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this monitor?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/monitors/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete monitor');
      await fetchMonitors();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/monitors/${id}/pause`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to pause monitor');
      await fetchMonitors();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/monitors/${id}/resume`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to resume monitor');
      await fetchMonitors();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRunNow = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/monitors/${id}/run`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to run monitor');
      const data = await response.json();
      alert(`Monitor run completed. Found ${data.new_papers_found} new paper(s).`);
      await fetchMonitors();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      query_string: '',
      filters_json: '',
      cadence: 'weekly',
      email_notifications: true,
      email_address: ''
    });
  };

  const openEditModal = (monitor) => {
    setEditingMonitor(monitor);
    setFormData({
      name: monitor.name,
      query_string: monitor.query_string || '',
      filters_json: monitor.filters_json || '',
      cadence: monitor.cadence,
      email_notifications: monitor.email_notifications,
      email_address: monitor.email_address || ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="monitors-page">
      <div className="monitors-header">
        <h1>Research Monitors</h1>
        <p>Automatically track new papers matching your searches</p>
        <button className="create-button" onClick={() => setShowCreateModal(true)}>
          <FiPlus /> Create Monitor
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading && !monitors.length && <div className="loading">Loading monitors...</div>}

      {!loading && monitors.length === 0 && (
        <div className="empty-state">
          <FiBell size={48} />
          <h2>No monitors yet</h2>
          <p>Create your first monitor to get email alerts when new papers match your research interests.</p>
          <button className="create-button" onClick={() => setShowCreateModal(true)}>
            <FiPlus /> Create Your First Monitor
          </button>
        </div>
      )}

      {monitors.length > 0 && (
        <div className="monitors-list">
          {monitors.map(monitor => (
            <div key={monitor.id} className="monitor-card">
              <div className="monitor-header">
                <div className="monitor-title">
                  <FiBell className={monitor.is_active ? 'active' : 'paused'} />
                  <h3>{monitor.name}</h3>
                  <span className={`status-badge ${monitor.is_active ? 'active' : 'paused'}`}>
                    {monitor.is_active ? 'Active' : 'Paused'}
                  </span>
                </div>
                <div className="monitor-actions">
                  <button onClick={() => handleRunNow(monitor.id)} title="Run Now">
                    <FiRefreshCw />
                  </button>
                  {monitor.is_active ? (
                    <button onClick={() => handlePause(monitor.id)} title="Pause">
                      <FiPause />
                    </button>
                  ) : (
                    <button onClick={() => handleResume(monitor.id)} title="Resume">
                      <FiPlay />
                    </button>
                  )}
                  <button onClick={() => openEditModal(monitor)} title="Edit">
                    <FiEdit2 />
                  </button>
                  <button onClick={() => handleDelete(monitor.id)} title="Delete">
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              <div className="monitor-details">
                <div className="detail-row">
                  <span className="label">Search Query:</span>
                  <span className="value">{monitor.query_string || 'None'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Frequency:</span>
                  <span className="value">{monitor.cadence === 'daily' ? 'Daily' : 'Weekly'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Last Run:</span>
                  <span className="value">{formatDate(monitor.last_run_at)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Next Run:</span>
                  <span className="value">{formatDate(monitor.next_run_at)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Last Results:</span>
                  <span className="value">{monitor.last_result_count} new paper(s)</span>
                </div>
                <div className="detail-row">
                  <span className="label">Email Notifications:</span>
                  <span className="value">{monitor.email_notifications ? 'On' : 'Off'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingMonitor) && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setEditingMonitor(null); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingMonitor ? 'Edit Monitor' : 'Create Monitor'}</h2>
              <button className="close-button" onClick={() => { setShowCreateModal(false); setEditingMonitor(null); resetForm(); }}>
                <FiX />
              </button>
            </div>
            <form onSubmit={editingMonitor ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label>Monitor Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., AI in Healthcare 2024"
                />
              </div>
              <div className="form-group">
                <label>Search Query</label>
                <input
                  type="text"
                  value={formData.query_string}
                  onChange={(e) => setFormData({ ...formData, query_string: e.target.value })}
                  placeholder="e.g., artificial intelligence healthcare"
                />
              </div>
              <div className="form-group">
                <label>Filters (JSON)</label>
                <textarea
                  value={formData.filters_json}
                  onChange={(e) => setFormData({ ...formData, filters_json: e.target.value })}
                  placeholder='{"publication_year": ">=2024", "type": "article"}'
                  rows={3}
                />
                <small>Optional: JSON format for OpenAlex filters</small>
              </div>
              <div className="form-group">
                <label>Frequency *</label>
                <select
                  value={formData.cadence}
                  onChange={(e) => setFormData({ ...formData, cadence: e.target.value })}
                  required
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.email_notifications}
                    onChange={(e) => setFormData({ ...formData, email_notifications: e.target.checked })}
                  />
                  Email Notifications
                </label>
              </div>
              {formData.email_notifications && (
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                    placeholder="Leave empty to use account email"
                  />
                </div>
              )}
              <div className="form-actions">
                <button type="button" onClick={() => { setShowCreateModal(false); setEditingMonitor(null); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : (editingMonitor ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Monitors;

